import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * Attach a fresh `Idempotency-Key` UUID to mutating requests targeting the
 * pipeline API. The pipeline-side `IdempotencyMiddleware` (Cosmos-backed cache,
 * 24h TTL) replays the cached response when the same key arrives twice —
 * preventing duplicate effects on accidental double-clicks or retried POSTs.
 *
 * Caller-supplied `Idempotency-Key` headers are preserved verbatim so
 * deliberate replays (e.g. operator-driven retries) still work.
 *
 * Important: we only attach to our own pipeline. Third-party endpoints (e.g.
 * Keycloak's /token) reject custom headers in the CORS preflight response,
 * which would block the OAuth code exchange.
 */
@Injectable()
export class IdempotencyKeyInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method)) {
      return next.handle(req);
    }
    if (req.headers.has('Idempotency-Key')) return next.handle(req);
    if (!isPipelineRequest(req.url)) return next.handle(req);
    const key = generateUuid();
    return next.handle(req.clone({ setHeaders: { 'Idempotency-Key': key } }));
  }
}

function isPipelineRequest(url: string): boolean {
  const base = environment.apis?.default?.url;
  if (base && url.startsWith(base)) return true;
  // Relative-path fallback (kept defensive in case the SPA ever issues
  // server-relative API calls).
  return url.startsWith('/v1/') || url.startsWith('/api/');
}

function generateUuid(): string {
  // Prefer the platform crypto.randomUUID (available in modern browsers + Node 19+).
  const cryptoObj = (globalThis as unknown as { crypto?: { randomUUID?: () => string } })
    .crypto;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();

  // Fallback: build a v4 UUID from getRandomValues. Karma headless Chrome
  // always supports randomUUID, but a manual fallback keeps the unit tests
  // robust against older test runners and avoids a hard dependency.
  const bytes = new Uint8Array(16);
  if (cryptoObj && (cryptoObj as { getRandomValues?: (b: Uint8Array) => Uint8Array }).getRandomValues) {
    (cryptoObj as { getRandomValues: (b: Uint8Array) => Uint8Array }).getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
